import React, { createContext, useContext, useState, useCallback } from "react";
import { supabase } from "../lib/supabase";
import { invokeFunction } from "../lib/invokeFunction";

const PaymentContext = createContext(null);

export function PaymentProvider({ children }) {
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentError, setPaymentError] = useState(null);

  const createPayment = useCallback(async (paymentData) => {
    setPaymentLoading(true);
    setPaymentError(null);

    try {
      const { data: result, error: fnError } = await invokeFunction("yookassa-create-payment", {
        body: {
          expertId: paymentData.expertId,
          clientId: paymentData.clientId,
          amountTotal: paymentData.amountTotal,
          amountExpert: paymentData.amountExpert,
          amountPlatform: paymentData.amountPlatform,
          commissionPercent: paymentData.commissionPercent,
          description: paymentData.description,
          returnUrl: paymentData.returnUrl,
          expertName: paymentData.expertName,
          clientName: paymentData.clientName,
          date: paymentData.date,
          time: paymentData.time,
          topic: paymentData.topic,
        }
      });

      if (fnError) {
        throw new Error(fnError.message || "Payment creation failed");
      }

      setPaymentLoading(false);
      return result;
    } catch (err) {
      console.error("Payment creation error:", err);
      setPaymentError(err.message);
      setPaymentLoading(false);
      throw err;
    }
  }, []);

  const checkPaymentStatus = useCallback(async (transactionId) => {
    try {
      const { data: result, error: fnError } = await invokeFunction("yookassa-check-payment", {
        body: { transactionId }
      });

      if (fnError) {
        throw new Error(fnError.message || "Payment check failed");
      }

      return result;
    } catch (err) {
      console.error("Check payment error:", err);
      throw err;
    }
  }, []);

  const getTransactionsForUser = useCallback(async (userId, role) => {
    const column = role === "expert" ? "expert_id" : "client_id";
    const { data, error } = await supabase
      .from("transactions")
      .select("*")
      .eq(column, userId)
      .order("created_at", { ascending: false });

    if (error) {
      console.warn("Failed to load transactions:", error);
      return [];
    }
    return data || [];
  }, []);

  return (
    <PaymentContext.Provider
      value={{
        paymentLoading,
        paymentError,
        createPayment,
        checkPaymentStatus,
        getTransactionsForUser,
      }}
    >
      {children}
    </PaymentContext.Provider>
  );
}

export function usePayment() {
  const context = useContext(PaymentContext);
  if (!context) {
    throw new Error("usePayment must be used within a PaymentProvider");
  }
  return context;
}
